import { useCallback, useEffect, useRef, useState } from 'react'
import { Pause, Play } from '@phosphor-icons/react'
import { useVideoPlayer } from '../../../hooks/media/useVideoPlayer'
import { useHotkeys } from '../../../hooks/media/useHotkeys'
import { useModalFitSize } from '../../../hooks/media/useModalFitSize'
import PlayerControls from './PlayerControls'

// Единый плеер для постов и чата. video.js внутри useVideoPlayer — только
// "мотор" (play/pause/seek/буферизация), весь визуал — PlayerControls, свои
// React-компоненты и css/video-player/video-player.css (Liquid Glass).
//
// variant="modal" — как в полноэкранном просмотре поста: сам вписывает себя
// в допустимую область экрана (см. useModalFitSize), с минимальной шириной
// под панель контролов.
// variant="inline" — как в чате: занимает 100% ширины родителя, высота — по
// пропорциям видео (через CSS aspect-ratio), родитель сам ограничивает ширину.
function VideoPlayer({
    src,
    variant = 'inline',
    autoPlay = false,
    muted = false,
    loop = false,
    enablePip = true,
    enableFullscreen = true,
    enableHotkeys = true,
    onPipChange,
}) {
    const player = useVideoPlayer({ src, autoPlay, muted, loop })
    const { videoRef, state, toggle, seekBy, setVolume, toggleMute } = player

    const containerRef = useRef(null)
    const [hovering, setHovering] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)

    // Пока видео ни разу не запускали — не показываем панель контролов вообще
    // (только кружок play поверх кадра), даже при наведении. Иначе в чате она
    // маячит поверх превью ещё до того, как человек вообще ткнул на видео.
    const [hasPlayedOnce, setHasPlayedOnce] = useState(autoPlay)
    useEffect(() => {
        if (state.playing) setHasPlayedOnce(true)
    }, [state.playing])

    // Тач-экран не умеет hover: без этого на телефоне панель контролов вообще
    // не появлялась — а тап по кадру просто переключал воспроизведение. Тап
    // показывает панель на несколько секунд, как в нативных плеерах.
    const [touchRevealed, setTouchRevealed] = useState(false)
    const revealTimerRef = useRef(null)

    const revealControls = useCallback(() => {
        setTouchRevealed(true)
        clearTimeout(revealTimerRef.current)
        revealTimerRef.current = setTimeout(() => setTouchRevealed(false), 3000)
    }, [])

    useEffect(() => () => clearTimeout(revealTimerRef.current), [])

    const controlsVisible = hasPlayedOnce && (hovering || touchRevealed || !state.playing)

    // В узком инлайн-варианте (чат) полный набор кнопок физически не помещается
    // и панель "разъезжается". Показываем компактный набор (play/прогресс/фулскрин),
    // а всё остальное (звук, скорость, PiP) становится доступно, как только человек
    // разворачивает видео на весь экран — там места уже достаточно.
    const compact = variant === 'inline' && !isFullscreen

    const fitSize = useModalFitSize(state.videoWidth, state.videoHeight)

    useEffect(() => {
        const onFsChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current)
        document.addEventListener('fullscreenchange', onFsChange)
        return () => document.removeEventListener('fullscreenchange', onFsChange)
    }, [])

    // useVideoPlayer уже сам слушает enterpictureinpicture/leavepictureinpicture
    // и кладёт результат в state.isPip — тут просто прокидываем это наружу тем,
    // кому нужно на это реагировать (пост прячет свою модалку, пока идёт PiP).
    useEffect(() => {
        onPipChange?.(state.isPip)
    }, [state.isPip, onPipChange])

    // Fullscreen — на своём контейнере (видео + панель контролов вместе), а не
    // через video.js: у video.js есть свой requestFullscreen(), но он разворачивает
    // только внутренний div самого плеера, а наша панель лежит рядом с ним, а не
    // внутри — в fullscreen она осталась бы недоступной.
    //
    // Fullscreen и PiP одновременно браузеры обрабатывают плохо (виджет
    // "видео вынесено в отдельное окно" рисуется криво/маленьким) — поэтому
    // сами не даём им пересекаться: включая один режим, сначала гарантированно
    // выключаем другой.
    const requestFullscreen = async () => {
        if (document.pictureInPictureElement) {
            await document.exitPictureInPicture()
        }
        if (document.fullscreenElement) {
            document.exitFullscreen()
        } else {
            containerRef.current?.requestFullscreen()
        }
    }

    const requestPip = async () => {
        if (document.fullscreenElement) {
            await document.exitFullscreen()
        }
        player.requestPip()
    }

    const handleKeyDown = useHotkeys({
        enabled: enableHotkeys,
        toggle,
        seekBy,
        setVolume,
        toggleMute,
        requestFullscreen,
        volume: state.volume,
    })

    const containerStyle = variant === 'modal'
        ? (fitSize ? { width: fitSize.width, height: fitSize.height } : { width: '90vw', maxWidth: 960 })
        : { width: '100%', aspectRatio: state.videoWidth && state.videoHeight ? `${state.videoWidth} / ${state.videoHeight}` : '16 / 9' }

    return (
        <div
            ref={containerRef}
            className="player-container"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onClick={(e) => {
                // Клик мимо элементов управления НЕ переключает воспроизведение —
                // он только показывает панель. Play/pause висит на центральном
                // кружке: случайный клик по кадру больше не останавливает видео,
                // а на телефоне тап по кадру — единственный способ достать панель.
                if (e.target.closest('.player-controls') || e.target.closest('.player-center-button')) return
                revealControls()
            }}
            style={{ ...containerStyle, outline: 'none' }}
        >
            <video ref={videoRef} className="player-video-el video-js" playsInline />

            {/* Центральный кружок дублирует play/pause из панели и работает всё
                время, пока панель видна: переключение воспроизведения живёт
                именно здесь, в явной зоне, а не на всём кадре. До первого
                запуска он виден всегда — как приглашение «нажми, чтобы начать». */}
            <button
                className={`player-center-button${hasPlayedOnce && !controlsVisible ? ' player-center-button--hidden' : ''}`}
                onClick={toggle}
                title={state.playing ? 'Пауза (space)' : 'Play (space)'}
            >
                {state.playing ? <Pause size={24} weight="fill" /> : <Play size={24} weight="fill" />}
            </button>

            <PlayerControls
                player={player}
                visible={controlsVisible}
                compact={compact}
                enablePip={enablePip}
                enableFullscreen={enableFullscreen}
                requestFullscreen={requestFullscreen}
                requestPip={requestPip}
            />
        </div>
    )
}

export default VideoPlayer
